import "./datapicker.scss";
import React from "react";
import { type Moment } from "moment";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import Button from "../../Buttons/Button";
import { type CalendarModeType } from "../CalendarModeSelection";
import DatePickerMonth from "./DatePickerMonth";

type Props = {
    mobile: boolean;
    range?: boolean;
    start: Moment | null;
    end: Moment | null;
    viewing: Moment;
    hover: Moment | null;
    onClick: (value: Moment) => void;
    onView: (viewing: Moment) => void;
    onModeChange: (mode: CalendarModeType) => void;
    onHoverChange: (hover: Moment | null) => void;
};

type State = {
    animate: boolean;
    direction: "left" | "right" | null;
    next: Moment;
};

export default class DatePicker extends React.Component<Props, State> {
    public _animating: null | ReturnType<typeof setTimeout>;

    constructor(props: Readonly<Props>) {
        super(props);

        this.state = {
            animate  : false,
            direction: null,
            next     : props.viewing.clone().add(1, "M"),
        };
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(prevProps.viewing !== this.props.viewing) {
            this.setState({
                next: this.props.viewing.clone().add(1, "M"),
            });
        }
    }

    public componentWillUnmount(): void {
        if(this._animating) {
            clearTimeout(this._animating);
        }
    }

    public animateChange = (direction: "left" | "right"): void => {
        if(this.state.animate) {
            return;
        }

        this.setState({
            animate: true,
            direction,
        }, () => {
            this._animating = setTimeout(() => {
                if(direction === "left") {
                    this.props.onView(this.props.viewing.clone().subtract(1, "M"));
                } else {
                    this.props.onView(this.state.next);
                }

                this.setState({
                    animate  : false,
                    direction: null,
                });
            }, 300);
        });
    };

    public handleMonthPrevious = (): void => {
        this.animateChange("left");
    };

    public handleMonthNext = (): void => {
        this.animateChange("right");
    };

    public handleMonthClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
        e.stopPropagation();
        this.props.onModeChange("month");
    };

    public renderAnimating = (right: boolean): React.ReactNode => {
        if(this.state.animate) {
            if(right && this.state.direction === "right") {
                if(this.props.mobile) {
                    return (
                        <DatePickerMonth
                            month={this.state.next}
                            range={!!this.props.range}
                            start={this.props.start}
                            end={this.props.end}
                            hover={this.props.hover}
                            onClick={this.props.onClick}
                            onHeaderClick={this.handleMonthClick}
                            onHoverChange={this.props.onHoverChange}
                        />
                    );
                }

                const after = this.state.next.clone().add(1, "M");

                return (
                    <DatePickerMonth
                        month={after}
                        range={!!this.props.range}
                        start={this.props.start}
                        end={this.props.end}
                        hover={this.props.hover}
                        onClick={this.props.onClick}
                        onHeaderClick={this.handleMonthClick}
                        onHoverChange={this.props.onHoverChange}
                    />
                );
            } else if(!right && this.state.direction === "left") {
                const before = this.props.viewing.clone().subtract(1, "M");

                return (
                    <DatePickerMonth
                        month={before}
                        range={!!this.props.range}
                        start={this.props.start}
                        end={this.props.end}
                        hover={this.props.hover}
                        onClick={this.props.onClick}
                        onHeaderClick={this.handleMonthClick}
                        onHoverChange={this.props.onHoverChange}
                    />
                );
            }
        }

        return (
            <div className="calendar-animate-shell" />
        );
    };

    public render(): React.ReactNode {
        const classes = [ "calendar-date-picker-months" ];

        if(this.state.animate && this.state.direction) {
            classes.push("animating", this.state.direction);
        }

        return (
            <div className="calendar-date-picker">
                <div className="calendar-date-picker-header">
                    <Button
                        type="button"
                        classes={[ "calendar-month-change previous" ]}
                        onClick={this.handleMonthPrevious}
                    >
                        <FontAwesomeLight icon="angle-left" />
                    </Button>
                    <Button
                        type="button"
                        classes={[ "calendar-month-change next" ]}
                        onClick={this.handleMonthNext}
                    >
                        <FontAwesomeLight icon="angle-right" />
                    </Button>
                </div>
                <div className={classes.join(" ")}>
                    {this.renderAnimating(false)}
                    <DatePickerMonth
                        month={this.props.viewing}
                        range={!!this.props.range}
                        start={this.props.start}
                        end={this.props.end}
                        hover={this.props.hover}
                        onClick={this.props.onClick}
                        onHeaderClick={this.handleMonthClick}
                        onHoverChange={this.props.onHoverChange}
                    />
                    {!this.props.mobile && (
                        <DatePickerMonth
                            month={this.state.next}
                            range={!!this.props.range}
                            start={this.props.start}
                            end={this.props.end}
                            hover={this.props.hover}
                            onClick={this.props.onClick}
                            onHeaderClick={this.handleMonthClick}
                            onHoverChange={this.props.onHoverChange}
                        />
                    )}
                    {this.renderAnimating(true)}
                </div>
            </div>
        );
    }
}
